import React from "react";
import { nanoid } from "nanoid";
import { Button, DropdownSearch, Select } from "ui";
import { XIcon } from "@heroicons/react/solid";
import { PlusCircleIcon } from "@heroicons/react/outline";
import { CONDITION_TYPES } from "../constants";
import { CURRENCIES } from "../../../constants/currencies";
import { getConditionReference } from "../utils/getConditionReference";

const currenciesOptions = CURRENCIES.map((currency) => [
  currency.currency_code,
  currency.currency_code,
]);

export function Conditions({
  availableConditions,
  conditions,
  error,
  updateConditions,
}) {
  function addCondition() {
    updateConditions([...conditions, { reference: nanoid() }]);
  }

  function updateCondition(reference, updatedCondition) {
    const updatedConditions = conditions.map((condition) => {
      if (getConditionReference(condition) === reference) {
        return updatedCondition;
      }

      return condition;
    });

    updateConditions(updatedConditions);
  }

  function removeCondition(reference) {
    const updatedConditions = conditions.filter(
      (condition) => getConditionReference(condition) !== reference
    );

    updateConditions(updatedConditions);
  }

  const alreadyAppliedConditionTypes = conditions?.map(
    (condition) => condition.resource_type
  );
  const availableConditionsOptions = availableConditions
    ?.map((condition) => [condition.resource_type, condition.type])
    .filter(
      ([availableConditionType]) =>
        !alreadyAppliedConditionTypes.includes(availableConditionType)
    );

  return (
    <div className="w-full rounded-md text-xs">
      <div className="flex flex-col gap-y-4">
        {conditions?.map((condition) => {
          const selectedConditionConfig = availableConditions.find(
            (availableCondition) =>
              availableCondition.resource_type === condition.resource_type
          );

          const conditionReference = getConditionReference(condition);
          return (
            <React.Fragment key={conditionReference}>
              <div className="group flex items-center justify-between space-x-4 rounded-md text-foundations-subtle">
                <Select
                  disabled={condition.isLocked}
                  className="mt-1 flex-1"
                  name="condition"
                  placeholder="Select condition"
                  selected={
                    condition.resource_type
                      ? [condition.resource_type, selectedConditionConfig.type]
                      : [null, null]
                  }
                  options={availableConditionsOptions}
                  onSelect={([value, valueText]) => {
                    const selectedCondition = availableConditions.find(
                      (condition) => condition.resource_type === value
                    );

                    updateCondition(conditionReference, {
                      ...condition,
                      resource_type: selectedCondition.resource_type,
                      type: selectedCondition.type,
                      label: valueText,
                      resource_data: {},
                    });
                  }}
                />
                <ConditionValueEdit
                  availableConditions={availableConditions}
                  condition={condition}
                  updateCondition={updateCondition}
                />

                {!condition.isLocked && (
                  <button onClick={() => removeCondition(conditionReference)}>
                    <XIcon className="mt-1 h-3 w-0 transition-width group-hover:w-3" />
                  </button>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {error && (
          <div className="flex flex-row-reverse text-sm text-red-700">
            {error}
          </div>
        )}

        <Button
          variant="secondary"
          onClick={addCondition}
          className="justify-start text-info"
        >
          <PlusCircleIcon />
          <p>
            {conditions.length > 0
              ? "Add another condition"
              : "Add your first condition"}
          </p>
        </Button>
      </div>
    </div>
  );
}

function ConditionValueEdit({
  availableConditions,
  condition,
  updateCondition,
}) {
  const conditionData = condition.resource_data;
  const selectedConditionConfig = availableConditions.find(
    (availableCondition) =>
      availableCondition.resource_type === condition.resource_type
  );

  if (!condition?.resource_type) return null;

  const conditionReference = getConditionReference(condition);

  if (condition.resource_type === CONDITION_TYPES.AMOUNT) {
    const operators = [
      ["=", "Equals"],
      [">=", "Greater than or equal"],
      ["<=", "Less than or equal"],
      [">", "Greater than"],
      ["<", "Less than"],
    ];

    return (
      <>
        <Select
          name="symbol"
          placeholder="Select symbol"
          className="max-w-[8rem] flex-1"
          placement="bottom-start"
          selected={mapOperatorToOption(conditionData?.operator) ?? ["", ""]}
          options={operators}
          onSelect={([value]) => {
            updateCondition(conditionReference, {
              ...condition,
              resource_data: {
                ...condition.resource_data,
                operator: mapOptionToOperator(value),
              },
            });
          }}
        />

        <input
          className="mt-1 w-full flex-1 rounded-md border-gray-300 text-sm placeholder-neutral-500 shadow-sm"
          type="number"
          placeholder="1000"
          value={conditionData?.value ?? ""}
          onChange={(event) => {
            updateCondition(conditionReference, {
              ...condition,
              resource_data: {
                ...condition.resource_data,
                value: Number(event.currentTarget.value),
              },
            });
          }}
        />

        <Select
          name="currency"
          placeholder="Select currency"
          className="max-w-[8rem] flex-1"
          contentClassName="max-w-[120px] text-center"
          placement="bottom-end"
          selected={
            conditionData?.currency
              ? [conditionData?.currency, conditionData?.currency]
              : ["", ""]
          }
          options={currenciesOptions}
          onSelect={([value]) => {
            updateCondition(conditionReference, {
              ...condition,
              resource_data: {
                ...condition.resource_data,
                currency: value,
              },
            });
          }}
        />
      </>
    );
  }

  const availableListOptions = selectedConditionConfig.results.map((list) => [
    list.list_id,
    list.list_name,
  ]);
  const selectedListOptions = selectedConditionConfig.results
    ?.filter((list) => conditionData?.bulk_lists?.includes?.(list.list_id))
    ?.map((list) => [list?.list_id, list?.list_name]);

  return (
    <DropdownSearch
      multiple
      name="bulk list"
      label="Select List"
      className="max-w-[8rem] flex-1"
      selected={selectedListOptions ?? []}
      options={availableListOptions}
      onSelect={(value) => {
        updateCondition(conditionReference, {
          ...condition,
          resource_data: {
            bulk_lists: value.map(([value]) => value),
            operator: "IN",
          },
        });
      }}
      contentProps={{ align: "end" }}
    />
  );
}

function mapOperatorToOption(operator) {
  const availableOperators = {
    EQUAL: ["=", "Equals"],
    GREATER_THAN: [">", "Greater than"],
    GREATER_THAN_OR_EQUAL: [">=", "Greater than or equal"],
    LESS_THAN: ["<", "Less than"],
    LESS_THAN_OR_EQUAL: ["<=", "Less than or equal"],
  };

  return availableOperators[operator];
}

function mapOptionToOperator(optionSymbol) {
  const availableOptions = {
    "=": "EQUAL",
    ">": "GREATER_THAN",
    ">=": "GREATER_THAN_OR_EQUAL",
    "<": "LESS_THAN",
    "<=": "LESS_THAN_OR_EQUAL",
  };

  return availableOptions[optionSymbol];
}
